import React, { useState, useEffect } from 'react';
import { Campaign } from '../../shared/types/entities';
import { Music, User, Building2, ExternalLink, Settings } from 'lucide-react';
import { supabase } from '../../backend/services/supabase';

interface CampaignAssociation {
  entity_type: 'release' | 'artist' | 'label';
  entity_name: string;
}

interface CampaignCardProps {
  campaign: Campaign;
  onManageAssociations?: (campaign: Campaign) => void;
  showAssociations?: boolean;
}

const CampaignCard: React.FC<CampaignCardProps> = ({ 
  campaign, 
  onManageAssociations,
  showAssociations = true 
}) => {
  const [associations, setAssociations] = useState<CampaignAssociation[]>([]);
  const [loadingAssociations, setLoadingAssociations] = useState(false);

  // Fetch campaign associations
  const fetchAssociations = async () => {
    if (!showAssociations) return;
    
    try {
      setLoadingAssociations(true);
      const session = await supabase.auth.getSession();
      const response = await fetch(`/api/campaigns/associations?campaign_id=${campaign.id}&campaign_type=${campaign.source || 'meta'}`, {
        headers: {
          'Authorization': `Bearer ${session.data.session?.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAssociations(data.associations || []);
      }
    } catch (error) {
      console.error('Error fetching associations:', error);
    } finally {
      setLoadingAssociations(false);
    }
  };

  useEffect(() => {
    if (showAssociations && campaign.id) {
      fetchAssociations();
    }
  }, [campaign.id, showAssociations]);

  // Get icon for entity type
  const getEntityIcon = (entityType: string) => {
    switch (entityType) {
      case 'release': return <Music className="h-3 w-3" />;
      case 'artist': return <User className="h-3 w-3" />;
      case 'label': return <Building2 className="h-3 w-3" />;
      default: return null;
    }
  };
  // Determine status color based on campaign status
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-500';
      case 'paused':
        return 'bg-yellow-500';
      case 'completed':
      case 'archived':
        return 'bg-red-500';
      case 'unknown':
        return 'bg-gray-400';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'Active';
      case 'paused': return 'Paused';
      case 'completed': return 'Completed';
      case 'archived': return 'Archived';
      case 'unknown': return 'Unknown';
      default: return status || 'Unknown';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow duration-200 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <h3 className="text-lg font-semibold flex-1" title={campaign.name}>
          {campaign.name.length > 25 ? `${campaign.name.substring(0, 25)}...` : campaign.name}
        </h3>
        <div className="flex items-center gap-2">
          {onManageAssociations && (
            <button
              onClick={() => onManageAssociations(campaign)}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Manage associations"
            >
              <Settings className="h-4 w-4" />
            </button>
          )}
          <span
            className={`w-3 h-3 rounded-full ${getStatusColor(campaign.status || 'unknown')}`}
            title={getStatusText(campaign.status || 'unknown')}
          />
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {getStatusText(campaign.status || 'unknown')}
          </span>
        </div>
      </div>

      {/* Associations */}
      {showAssociations && (
        <div className="border-b border-gray-200 dark:border-gray-700 pb-3">
          {loadingAssociations ? (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <div className="animate-spin h-3 w-3 border border-gray-300 border-t-transparent rounded-full"></div>
              Loading associations...
            </div>
          ) : associations.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {associations.slice(0, 3).map((association, index) => (
                <div
                  key={index}
                  className="flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs"
                  title={association.entity_type}
                >
                  {getEntityIcon(association.entity_type)}
                  <span className="truncate max-w-20">
                    {association.entity_name}
                  </span>
                </div>
              ))}
              {associations.length > 3 && (
                <div className="flex items-center px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-600 dark:text-gray-400">
                  +{associations.length - 3} more
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-400 dark:text-gray-500 italic">
              No associations
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <p className="text-gray-500 dark:text-gray-400">Spend</p>
          <p className="font-medium">
            {campaign.spend && campaign.spend > 0 
              ? `$${campaign.spend.toLocaleString()}` 
              : <span className="text-gray-400 italic">No data</span>
            }
          </p>
        </div>
        <div>
          <p className="text-gray-500 dark:text-gray-400">CTR</p>
          <p className="font-medium">
            {campaign.ctr && campaign.ctr > 0 
              ? `${campaign.ctr.toFixed(2)}%` 
              : <span className="text-gray-400 italic">No data</span>
            }
          </p>
        </div>
      </div>
      <div className="mt-auto">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <p className="text-gray-500 dark:text-gray-400">Impressions</p>
            <p className="font-medium">
              {campaign.impressions && campaign.impressions > 0 
                ? campaign.impressions.toLocaleString() 
                : <span className="text-gray-400 italic">No data</span>
              }
            </p>
          </div>
          <div>
            <p className="text-gray-500 dark:text-gray-400">Conversions</p>
            <p className="font-medium">
              {campaign.conversions && campaign.conversions > 0 
                ? campaign.conversions.toLocaleString() 
                : <span className="text-gray-400 italic">No data</span>
              }
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            <span className="text-blue-600 dark:text-blue-400">Meta Ads Campaign</span>
          </span>
          {campaign.meta_campaign_id && (
            <button
              onClick={() => window.open(`https://business.facebook.com/adsmanager/manage/campaigns/detail?act=${campaign.account_id}&selected_campaign_ids=${campaign.meta_campaign_id}`, '_blank')}
              className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
              title="View in Meta Ads Manager"
            >
              <ExternalLink className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignCard;
